import { Fragment, useEffect, useState } from 'react';
import styled from 'styled-components';
import classNames from 'classnames';
import { CALYPSO } from 'HubStyleTokens/colors';
import { INPUT_SM_FONT_SIZE, NAV_ITEM_PADDING_Y } from 'HubStyleTokens/sizes';
import AccordionTransition from 'UIComponents/transitions/AccordionTransition';
import UIClickable from 'UIComponents/button/UIClickable';
import UIIcon from 'UIComponents/icon/UIIcon';
import UICheckbox from 'UIComponents/input/UICheckbox';
import UIRadioInput from 'UIComponents/input/UIRadioInput';
import SyntheticEvent from 'UIComponents/core/SyntheticEvent';
import unescapedText from 'I18n/utils/unescapedText';
import { jsx as _jsx } from "react/jsx-runtime";
import { createElement as _createElement } from "react";
import { jsxs as _jsxs } from "react/jsx-runtime";
const INPUT_CLASS = 'filter-toggle-group__input';
const OptionGroupOpenIndicator = styled(UIIcon).withConfig({
  displayName: "FilterOption__OptionGroupOpenIndicator",
  componentId: "sc-1jp9b94-0"
})(["align-self:center;margin-left:auto;"]);
OptionGroupOpenIndicator.displayName = 'OptionGroupOpenIndicator';
export const OptionGroupText = styled.span.withConfig({
  displayName: "FilterOption__OptionGroupText",
  componentId: "sc-1jp9b94-1"
})(["font-size:", ";line-height:16px;padding-bottom:", ";padding-top:", ";"], INPUT_SM_FONT_SIZE, NAV_ITEM_PADDING_Y, NAV_ITEM_PADDING_Y);
OptionGroupText.displayName = 'OptionGroupText';
function OptionGroup(props) {
  const {
    forceOpen,
    LabelComponent,
    onGroupToggle,
    option,
    value
  } = props;
  const someChecked = option.options.some(nestedOption => value.includes(nestedOption.value));
  const allChecked = option.options.every(nestedOption => value.includes(nestedOption.value));
  const [open, setOpen] = useState(someChecked || !!forceOpen);
  useEffect(() => {
    if (forceOpen) setOpen(forceOpen);
  }, [forceOpen]);
  const handleGroupClick = () => setOpen(!open);
  const handleGroupChange = event => {
    if (event.source.target.checked) setOpen(true);
    onGroupToggle(event);
  };
  const handleLabelClick = e => {
    e.preventDefault();
    setOpen(!open);
  };
  const GroupLabelComponent = () => /*#__PURE__*/_jsx(UIClickable, {
    onClick: handleLabelClick,
    children: LabelComponent ? /*#__PURE__*/_jsx(LabelComponent, {
      option: option
    }) : option.text
  });
  const GroupItemComponent = () => option.selectAll ? /*#__PURE__*/_jsx(OptionItem, Object.assign({}, props, {
    checked: allChecked,
    LabelComponent: GroupLabelComponent,
    indeterminate: someChecked,
    onChange: handleGroupChange
  })) : /*#__PURE__*/_jsx(OptionGroupText, {
    children: option.text
  });
  if (option.flatLayout) {
    return /*#__PURE__*/_jsxs(Fragment, {
      children: [/*#__PURE__*/_jsx(GroupItemComponent, {}), option.options.map(nestedOption => /*#__PURE__*/_createElement(OptionItem, Object.assign({}, props, {
        checked: value.includes(nestedOption.value),
        key: nestedOption.value,
        nested: true,
        option: nestedOption
      })))]
    });
  }
  return /*#__PURE__*/_jsxs(Fragment, {
    children: [/*#__PURE__*/_jsxs(UIClickable, {
      "aria-expanded": open,
      onClick: handleGroupClick,
      style: {
        display: 'flex'
      },
      children: [/*#__PURE__*/_jsx(GroupItemComponent, {}), /*#__PURE__*/_jsx(OptionGroupOpenIndicator, {
        color: CALYPSO,
        name: open ? 'up' : 'down',
        size: "xxs"
      })]
    }), /*#__PURE__*/_jsx(AccordionTransition, {
      open: open,
      children: option.options.map(nestedOption => /*#__PURE__*/_createElement(OptionItem, Object.assign({}, props, {
        checked: value.includes(nestedOption.value),
        key: nestedOption.value,
        nested: true,
        option: nestedOption
      })))
    })]
  });
}
function OptionItem({
  checked,
  disabled,
  InputComponent,
  inputClassName,
  indeterminate,
  LabelComponent,
  name,
  nested,
  onChange,
  option,
  style = {}
}) {
  const label = LabelComponent ? /*#__PURE__*/_jsx(LabelComponent, {
    option: option
  }) : option.text;
  if (nested) style.paddingLeft = '12px';
  const handleChange = event => onChange(SyntheticEvent(option, event));
  const value = 'value' in option ? option.value : undefined;
  const ariaLabel = checked ? unescapedText('marketplace-ui-common.compatibility.planCompatibilityFilter.ariaLabelSelected', {
    option: option.text
  }) : unescapedText('marketplace-ui-common.compatibility.planCompatibilityFilter.ariaLabelSelect', {
    option: option.text
  });
  return /*#__PURE__*/_jsx(InputComponent, {
    "aria-label": ariaLabel,
    checked: checked,
    className: classNames(INPUT_CLASS, inputClassName),
    "data-checked": checked,
    "data-test-id": `filter-toggle-input::${name}::${value}`,
    disabled: disabled,
    indeterminate: indeterminate,
    name: name,
    onClick: e => e.stopPropagation(),
    onChange: handleChange,
    style: style,
    value: value,
    children: label
  });
}
export default function FilterToggle(props) {
  const {
    multi,
    onChange,
    option
  } = props;
  const InputComponent = multi ? UICheckbox : UIRadioInput;
  const handleChange = event => {
    const checked = event.source.target.checked;
    const eventValue = event.target.value.value;
    if (multi) {
      const _value = props.value === undefined ? [] : props.value;
      const value = Array.isArray(_value) ? _value : [_value];
      const newValue = checked ? value.concat([eventValue]) : value.filter(v => v !== eventValue);
      const newEvent = SyntheticEvent(newValue, event.source);
      onChange(newEvent);
    } else {
      const newEvent = SyntheticEvent(eventValue, event.source);
      onChange(newEvent);
    }
  };
  const isGroupOption = ('options' in option);
  if (isGroupOption) {
    const _value = props.value === undefined ? [] : props.value;
    const value = Array.isArray(_value) ? _value : [_value];
    if (option.flatLayout) {
      return /*#__PURE__*/_jsx(OptionGroup, Object.assign({}, props, {
        InputComponent: InputComponent,
        onGroupToggle: () => {},
        onChange: handleChange,
        option: option,
        value: value
      }));
    }
    const handleGroupToggle = event => {
      const checked = event.source.target.checked;
      const groupValues = option.options.map(o => o.value);
      const valueMinusGroup = value.filter(v => !groupValues.includes(v));
      const newValue = checked ? valueMinusGroup.concat(groupValues) : valueMinusGroup;
      const newEvent = SyntheticEvent(newValue, event.source);
      onChange(newEvent);
    };
    return /*#__PURE__*/_jsx(OptionGroup, Object.assign({}, props, {
      InputComponent: InputComponent,
      onGroupToggle: handleGroupToggle,
      onChange: handleChange,
      option: option,
      value: value
    }));
  } else {
    const value = props.value;
    const checked = Array.isArray(value) ? value.includes(option.value) : value === option.value;
    return /*#__PURE__*/_jsx(OptionItem, Object.assign({}, props, {
      checked: checked,
      InputComponent: InputComponent,
      onChange: handleChange,
      option: option
    }));
  }
}